/**
 * Solution arithmetic: molarity, dilution, percent solutions and parts-per notation.
 *
 * Everything here takes and returns base units — grams, litres, mol/L, g/mol — and
 * returns `null` instead of a nonsense number (division by zero, a negative quantity).
 * Unit conversion is `units.ts`; this file is only the relationships.
 */

const positive = (...values: number[]) =>
  values.every((v) => Number.isFinite(v) && v > 0);

/* ------------------------------------------------------------- molarity */

/**
 *   c = n / V,  n = m / M
 *
 * c molarity (mol/L), n amount (mol), V solution volume (L), m solute mass (g),
 * M molar mass (g/mol).
 */
export function molarityFrom(
  massG: number,
  molarMass: number,
  volumeL: number
): number | null {
  return positive(massG, molarMass, volumeL)
    ? massG / molarMass / volumeL
    : null;
}

/** m = c × V × M */
export function massFrom(
  molarity: number,
  volumeL: number,
  molarMass: number
): number | null {
  return positive(molarity, volumeL, molarMass)
    ? molarity * volumeL * molarMass
    : null;
}

/** V = m / (M × c) */
export function volumeFrom(
  massG: number,
  molarMass: number,
  molarity: number
): number | null {
  return positive(massG, molarMass, molarity)
    ? massG / molarMass / molarity
    : null;
}

/** M = m / (c × V) */
export function molarMassFrom(
  massG: number,
  molarity: number,
  volumeL: number
): number | null {
  return positive(massG, molarity, volumeL)
    ? massG / (molarity * volumeL)
    : null;
}

export type MolaritySolve = "molarity" | "mass" | "volume" | "molarMass";

export interface MolarityValues {
  /** g */
  massG: number;
  /** L */
  volumeL: number;
  /** mol/L */
  molarity: number;
  /** g/mol */
  molarMass: number;
  /** mol */
  moles: number;
}

/**
 * All five quantities, given any three of mass, volume, molarity and molar mass.
 * `known` need only hold the three that are not `solve`; null if any is unusable.
 */
export function solveMolarity(
  solve: MolaritySolve,
  known: Partial<Omit<MolarityValues, "moles">>
): MolarityValues | null {
  const { massG, volumeL, molarity, molarMass } = known;
  const num = (value: number | undefined) => value ?? Number.NaN;
  let result: Omit<MolarityValues, "moles"> | null = null;
  if (solve === "molarity") {
    const c = molarityFrom(num(massG), num(molarMass), num(volumeL));
    result = c === null ? null : { ...(known as MolarityValues), molarity: c };
  } else if (solve === "mass") {
    const m = massFrom(num(molarity), num(volumeL), num(molarMass));
    result = m === null ? null : { ...(known as MolarityValues), massG: m };
  } else if (solve === "volume") {
    const v = volumeFrom(num(massG), num(molarMass), num(molarity));
    result = v === null ? null : { ...(known as MolarityValues), volumeL: v };
  } else {
    const mm = molarMassFrom(num(massG), num(molarity), num(volumeL));
    result =
      mm === null ? null : { ...(known as MolarityValues), molarMass: mm };
  }
  if (result === null) {
    return null;
  }
  return { ...result, moles: result.molarity * result.volumeL };
}

/* ------------------------------------------------------------- dilution */

export type DilutionTerm = "c1" | "v1" | "c2" | "v2";

export type DilutionValues = Record<DilutionTerm, number>;

/**
 * C₁V₁ = C₂V₂ solved for `unknown`; the other three terms must be positive.
 * Concentrations in one unit, volumes in one unit.
 */
export function solveDilution(
  unknown: DilutionTerm,
  known: Partial<DilutionValues>
): number | null {
  const get = (term: DilutionTerm): number => known[term] ?? Number.NaN;
  switch (unknown) {
    case "c1":
      return positive(get("v1"), get("c2"), get("v2"))
        ? (get("c2") * get("v2")) / get("v1")
        : null;
    case "v1":
      return positive(get("c1"), get("c2"), get("v2"))
        ? (get("c2") * get("v2")) / get("c1")
        : null;
    case "c2":
      return positive(get("c1"), get("v1"), get("v2"))
        ? (get("c1") * get("v1")) / get("v2")
        : null;
    default:
      return positive(get("c1"), get("v1"), get("c2"))
        ? (get("c1") * get("v1")) / get("c2")
        : null;
  }
}

/* ------------------------------------------------------ percent solutions */

/** w/v: g per 100 mL · w/w: g per 100 g · v/v: mL per 100 mL. See `percent.ts`. */
export type PercentKind = "wv" | "ww" | "vv";
